import type {
    IGetGamesBySlugsQuery,
    IMetaTags,
    IProductMetadata,
} from '../../gameplay/queries/gameplay.graphql';

import { getAssetUrl } from './get-asset-url';

export function convertProductToStructuredData(
    data?: IProductMetadata | null,
    game?: NonNullable<IGetGamesBySlugsQuery['getGamesBySlugs']>[0],
) {
    return {
        tag: 'script',
        type: 'application/ld+json',
        content: JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Product',
            'name': data?.name ?? game?.name,
            'image': [data?.image ?? game?.thumbnails[0]?.background],
            'description': data?.description ?? game?.description,
            'category': data?.category ?? game?.category,
            'brand': {
                '@type': 'Brand',
                'name': data?.brand?.name ?? game?.gameVendor?.name,
                'logo': data?.brand?.logo ?? game?.gameVendor?.logo,
            },
            'review': {
                '@type': 'Review',
                'reviewBody': data?.review?.reviewBody,
                'name': data?.review?.name,
                'reviewRating': {
                    '@type': 'Rating',
                    'ratingValue': data?.review?.rating?.ratingValue ?? game?.rating,
                    'bestRating': data?.review?.rating?.bestValue ?? '100',
                    'worstRating': data?.review?.rating?.worstRating ?? '0',
                },
                'author': data?.review?.author?.name
                    ? {
                          '@type': 'Person',
                          'name': data?.review?.author?.name,
                      }
                    : undefined,
            },
        }),
    } as const;
}

export function getMetatagsFromMetadata(
    metadata?: IMetaTags | null,
    initial?: Partial<{
        pageTitle: string | null;
        description: string | null;
        keywords: string | null;
    }>,
) {
    const title = metadata?.pageTitle ?? initial?.pageTitle;
    const description = metadata?.description ?? initial?.description;
    const keywords = metadata?.keywords ?? initial?.keywords;
    const ogDescription = metadata?.ogDescription ?? description;
    const ogTitle = metadata?.ogTitle ?? title;
    const ogType = metadata?.ogType ?? 'website';
    const ogImage = getAssetUrl(metadata?.ogImage);
    const ogUrl = metadata?.ogUrl;

    return [
        title
            ? ({
                  tag: 'title',
                  content: title,
              } as const)
            : null,
        description
            ? ({
                  tag: 'meta',
                  name: 'description',
                  content: description,
              } as const)
            : null,
        keywords
            ? ({
                  tag: 'meta',
                  name: 'keywords',
                  content: keywords,
              } as const)
            : null,
        ogTitle
            ? ({
                  tag: 'meta',
                  property: 'og:title',
                  content: ogTitle,
              } as const)
            : null,
        ogDescription
            ? ({
                  tag: 'meta',
                  property: 'og:description',
                  content: ogDescription,
              } as const)
            : null,
        ogType
            ? ({
                  tag: 'meta',
                  property: 'og:type',
                  content: ogType,
              } as const)
            : null,
        ogUrl
            ? ({
                  tag: 'meta',
                  property: 'og:url',
                  content: ogUrl,
              } as const)
            : null,
        ogImage
            ? ({
                  tag: 'meta',
                  property: 'og:image',
                  content: ogImage,
              } as const)
            : null,
    ] as const;
}
